import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';
import moment from 'moment';

import { useTheme } from '../../ThemeContext';

import Select from '../Select';
import DayPicker from '../DayPicker';

import {
  Wrapper,
  Holder,
  ToggleBox,
  Toggle,
  Header,
  Title,
  Content,
  Grow,
  Footer,
  Button,
  Item,
  Inputbox,
  Input,
  Info,
  ResetFilterButton,
  ResetFilterButtonIcon,
  SelectionHolder,
  Selection,
  SelectionInput,
  SelectionCheckmark,
} from './Filter.style';

const Filter = ({
  availableFilterAnnotated = false,
  availableFilterVerified = false,
  availableFilterRejected = false,
  availableFilterReserved = false,
  availableFilterTager = false,
  availableFilterMultilabeling = false,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const wrapperRef = useRef();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const filterStore = useSelector((state) => state.filterStore);
  const dispatch = useDispatch();
  const dataSources = useSelector(
    (state) => state.firestore.ordered['data_sources']
  );
  const filterDataSourcePhotoCategories = useSelector(
    (state) =>
      filterStore.dataSource &&
      state.firestore.ordered[
        `data_sources/${filterStore.dataSource}/photo_categories`
      ]
  );
  const filterDataSourceObjectsCategories = useSelector(
    (state) =>
      filterStore.dataSource &&
      state.firestore.ordered[
        `data_sources/${filterStore.dataSource}/objects_categories`
      ]
  );

  const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
      if (isOpenMenu) {
        if (
          ref.current &&
          !ref.current.contains(e.target) &&
          !(
            e.target.getAttribute('id') &&
            e.target.getAttribute('id').includes('react-select')
          )
        ) {
          callback();
        }
      }
    };

    useEffect(() => {
      document.addEventListener('click', handleClick);

      return () => {
        document.removeEventListener('click', handleClick);
      };
    });
  };

  const handleOpenMenu = () => setIsOpenMenu(!isOpenMenu);

  const handleResetFilter = () => dispatch({ type: 'FILTER_SET', payload: {} });

  const handleChangeFilter = (filters) => {
    return dispatch({
      type: 'FILTER_SET',
      payload: { ...filterStore, ...filters },
    });
  };

  useOutsideClick(wrapperRef, () => isOpenMenu && setIsOpenMenu(false));

  useEffect(() => {
    if (
      Object.entries(filterStore).filter(([type, value]) => {
        if (type === 'annotated' && !availableFilterAnnotated) return false;
        else if (type === 'verified' && !availableFilterVerified) return false;
        else if (type === 'rejected' && !availableFilterRejected) return false;
        else if (type === 'reserved' && !availableFilterReserved) return false;
        else if (type === 'user_id' && !availableFilterTager) return false;
        else if (type === 'multilabeling' && !availableFilterMultilabeling)
          return false;
        else if (value !== undefined) return true;
        else return false;
      }).length > 0
    )
      setIsFiltersActive(true);
    else setIsFiltersActive(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStore]);

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        { collection: 'data_sources', orderBy: [['name', 'asc']] },
      ],
      storeAs: 'data_sources',
    });
  }

  if (isProfileCompany && filterStore.dataSource) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: filterStore.dataSource,
          subcollections: [{ collection: 'photo_categories' }],
        },
      ],
      storeAs: `data_sources/${filterStore.dataSource}/photo_categories`,
    });

    if (availableFilterMultilabeling) {
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'data_sources',
            doc: filterStore.dataSource,
            subcollections: [{ collection: 'objects_categories' }],
          },
        ],
        storeAs: `data_sources/${filterStore.dataSource}/objects_categories`,
      });
    }
  }
  useFirestoreConnect(firestoreConnect);

  const renderToggle = () => {
    return (
      <ToggleBox
        theme={theme.filter.toggleBox}
        isActive={isFiltersActive}
        isToggle={isOpenMenu}
      >
        <Toggle
          theme={theme.filter.toggle}
          isActive={isFiltersActive}
          onClick={() => handleOpenMenu()}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='21.952'
            height='21.952'
            viewBox='0 0 21.952 21.952'
          >
            <path
              id='filter-24px'
              d='M14.34,3.2C8.307,3.2,3.3,5.382,3.3,8.078a3.064,3.064,0,0,0,1.284,2.311l7.189,6.676v7.061c0,.514,1.155,1.027,2.568,1.027s2.568-.385,2.568-1.027V17.193l7.061-6.8a2.721,2.721,0,0,0,1.284-2.311C25.252,5.382,20.374,3.2,14.34,3.2Zm0,7.831C9.334,11.031,5.1,9.747,5.1,8.078S9.205,5.126,14.34,5.126s9.243,1.284,9.243,2.953S19.347,11.031,14.34,11.031Z'
              transform='translate(-3.3 -3.2)'
            />
          </svg>
        </Toggle>
      </ToggleBox>
    );
  };

  const renderResetFilterButton = ({ isVisible, onClick }) => {
    return (
      <ResetFilterButton
        theme={theme.filter.resetFilterButton}
        title={intl.formatMessage({ id: 'FILTER.CLEAR' })}
        isVisible={isVisible}
        onClick={onClick}
      >
        <ResetFilterButtonIcon className='material-icons'>
          clear
        </ResetFilterButtonIcon>
      </ResetFilterButton>
    );
  };

  const renderMultilabelingSelection = () => {
    return (
      <SelectionHolder
        theme={theme.filter.selectionHolder}
        onClick={(e) => {
          e.stopPropagation();
          handleChangeFilter({
            multilabeling: !filterStore.multilabeling,
          });
        }}
      >
        <Selection
          theme={theme.filter.selection}
          isSelected={filterStore.multilabeling ? true : undefined}
        >
          <SelectionInput
            readOnly
            type='checkbox'
            defaultChecked={filterStore.multilabeling ? true : undefined}
          />
          <SelectionCheckmark
            theme={theme.filter.selectionCheckmark}
            className='material-icons'
            isSelected={filterStore.multilabeling ? true : undefined}
          >
            check
          </SelectionCheckmark>
        </Selection>
        {intl.formatMessage({ id: 'FILTER.MULTILABELING' })}
      </SelectionHolder>
    );
  };

  return (
    <Wrapper
      ref={wrapperRef}
      theme={theme.filter.wrapper}
      isHide={!isOpenMenu ? true : undefined}
    >
      {renderToggle()}
      <Holder theme={theme.filter.holder}>
        <Header theme={theme.filter.header}>
          <Title theme={theme.filter.title}>
            {intl.formatMessage({
              id: 'FILTER.FILTERS',
            })}
          </Title>
        </Header>
        <Content theme={theme.filter.content}>
          <Item>
            <Inputbox>
              <Input
                theme={theme.input.default}
                type='text'
                placeholder={intl.formatMessage({
                  id: 'FILTER.INPUT.ORIGIN',
                })}
                value={filterStore.origin || ''}
                onChange={(event) =>
                  handleChangeFilter({
                    origin: event.target.value ? event.target.value : undefined,
                  })
                }
              />
            </Inputbox>
            {renderResetFilterButton({
              isVisible: filterStore.origin !== undefined ? true : undefined,
              onClick: () => handleChangeFilter({ origin: undefined }),
            })}
          </Item>
          <Item>
            <Inputbox>
              <DayPicker
                selectedDate={filterStore.startDate}
                maxDate={filterStore.endDate}
                placeholder={intl.formatMessage({
                  id: 'FILTER.INPUT.DATE_FROM',
                })}
                onSelectDate={(value) =>
                  handleChangeFilter({
                    startDate: value
                      ? moment.unix(value).startOf('day').unix()
                      : undefined,
                  })
                }
              />
            </Inputbox>
            {renderResetFilterButton({
              isVisible: filterStore.startDate !== undefined ? true : undefined,
              onClick: () => handleChangeFilter({ startDate: undefined }),
            })}
          </Item>
          <Item>
            <Inputbox>
              <DayPicker
                selectedDate={filterStore.endDate}
                minDate={filterStore.startDate}
                placeholder={intl.formatMessage({
                  id: 'FILTER.INPUT.DATE_TO',
                })}
                onSelectDate={(value) =>
                  handleChangeFilter({
                    endDate: value
                      ? moment.unix(value).endOf('day').unix()
                      : undefined,
                  })
                }
              />
            </Inputbox>
            {renderResetFilterButton({
              isVisible: filterStore.endDate !== undefined ? true : undefined,
              onClick: () => handleChangeFilter({ endDate: undefined }),
            })}
          </Item>
          {availableFilterAnnotated && (
            <Item>
              <Inputbox>
                <Select
                  value={filterStore.annotated}
                  options={[
                    {
                      value: false,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.FALSE',
                      }),
                    },
                    {
                      value: true,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.TRUE',
                      }),
                    },
                  ]}
                  placeholderMessage={'FILTER.INPUT.ANNOTATED'}
                  onChange={(value) => handleChangeFilter({ annotated: value })}
                />
              </Inputbox>
              {renderResetFilterButton({
                isVisible:
                  filterStore.annotated !== undefined ? true : undefined,
                onClick: () => handleChangeFilter({ annotated: undefined }),
              })}
            </Item>
          )}
          {availableFilterVerified && (
            <Item>
              <Inputbox>
                <Select
                  value={filterStore.verified}
                  options={[
                    {
                      value: false,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.FALSE',
                      }),
                    },
                    {
                      value: true,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.TRUE',
                      }),
                    },
                  ]}
                  placeholderMessage={'FILTER.INPUT.VERIFIED'}
                  onChange={(value) => handleChangeFilter({ verified: value })}
                />
              </Inputbox>
              {renderResetFilterButton({
                isVisible:
                  filterStore.verified !== undefined ? true : undefined,
                onClick: () => handleChangeFilter({ verified: undefined }),
              })}
            </Item>
          )}
          {availableFilterRejected && (
            <Item>
              <Inputbox>
                <Select
                  value={filterStore.rejected}
                  options={[
                    {
                      value: false,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.FALSE',
                      }),
                    },
                    {
                      value: true,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.TRUE',
                      }),
                    },
                  ]}
                  placeholderMessage={'FILTER.INPUT.REJECTED'}
                  onChange={(value) => handleChangeFilter({ rejected: value })}
                />
              </Inputbox>
              {renderResetFilterButton({
                isVisible:
                  filterStore.rejected !== undefined ? true : undefined,
                onClick: () => handleChangeFilter({ rejected: undefined }),
              })}
            </Item>
          )}
          {availableFilterReserved && (
            <Item>
              <Inputbox>
                <Select
                  value={filterStore.reserved}
                  options={[
                    {
                      value: false,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.FALSE',
                      }),
                    },
                    {
                      value: true,
                      label: intl.formatMessage({
                        id: 'FILTER.INPUT.VALUE.TRUE',
                      }),
                    },
                  ]}
                  placeholderMessage={'FILTER.INPUT.RESERVED'}
                  onChange={(value) => handleChangeFilter({ reserved: value })}
                />
              </Inputbox>
              {renderResetFilterButton({
                isVisible:
                  filterStore.reserved !== undefined ? true : undefined,
                onClick: () => handleChangeFilter({ reserved: undefined }),
              })}
            </Item>
          )}
          {availableFilterTager && (
            <Item>
              <Inputbox>
                <Select
                  value={filterStore.user_id}
                  options={[
                    {
                      value: 'rerKmtJbvdQ4pStEP5fuFA1X9yt2',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.rerKmtJbvdQ4pStEP5fuFA1X9yt2',
                      }),
                    },
                    {
                      value: 'm0xBN2RghBVlqomytbLyWsODvrK2',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.m0xBN2RghBVlqomytbLyWsODvrK2',
                      }),
                    },
                    {
                      value: 'NAChx01uiUURedzEUWYsIIqdDdo1',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.NAChx01uiUURedzEUWYsIIqdDdo1',
                      }),
                    },
                    {
                      value: 'GvAYD2ZrCwX2QGyXbvB9EhXJCNh2',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.GvAYD2ZrCwX2QGyXbvB9EhXJCNh2',
                      }),
                    },
                    {
                      value: '1FSNIRy8lFNf0CedCwxYTkgXLg52',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.1FSNIRy8lFNf0CedCwxYTkgXLg52',
                      }),
                    },
                    {
                      value: 'UkrdBKvY70dTbvPJ2kWQtUBm5r13',
                      label: intl.formatMessage({
                        id:
                          'FILTER.INPUT.VALUE.USER.UkrdBKvY70dTbvPJ2kWQtUBm5r13',
                      }),
                    },
                  ]}
                  placeholderMessage={'FILTER.INPUT.TAGER'}
                  onChange={(value) => handleChangeFilter({ user_id: value })}
                />
              </Inputbox>
              {renderResetFilterButton({
                isVisible: filterStore.user_id !== undefined ? true : undefined,
                onClick: () => handleChangeFilter({ user_id: undefined }),
              })}
            </Item>
          )}
          <Item>
            <Inputbox>
              <Select
                value={filterStore.dataSource}
                options={
                  dataSources &&
                  dataSources.length > 0 &&
                  dataSources.map((dataSource) => {
                    return {
                      ...dataSource,
                      value: dataSource.id,
                      label: dataSource.name,
                    };
                  })
                }
                placeholderMessage={'FILTER.INPUT.DATA_SOURCE'}
                onChange={(value) =>
                  handleChangeFilter({
                    dataSource: value,
                    recognition: undefined,
                    tags: undefined,
                    multilabeling: undefined,
                  })
                }
                isLoading={!isLoaded(dataSources)}
                isSearchable={true}
                menuPosition={'relative'}
              />
            </Inputbox>
            {renderResetFilterButton({
              isVisible:
                filterStore.dataSource !== undefined ? true : undefined,
              onClick: () =>
                handleChangeFilter({
                  dataSource: undefined,
                  recognition: undefined,
                  tags: undefined,
                  multilabeling: undefined,
                }),
            })}
          </Item>
          {!filterStore.dataSource ? (
            <Info theme={theme.filter.info}>
              {intl.formatMessage({
                id:
                  'FILTER.YOU_NEED_TO_CHOOSE_A_DATA_SOURCE_TO_SEE_ALL_AVAILABLE_FILTERS',
              })}
            </Info>
          ) : (
            <>
              {filterDataSourcePhotoCategories &&
              isLoaded(filterDataSourcePhotoCategories) &&
              filterDataSourcePhotoCategories.length > 0 ? (
                <>
                  <Item>
                    <Inputbox>
                      <Select
                        value={filterStore.recognition}
                        options={[
                          {
                            value: 'automatic_annotation',
                            label: intl.formatMessage({
                              id: 'FILTER.INPUT.VALUE.AUTOMATIC',
                            }),
                          },
                          {
                            value: 'vinne_annotation',
                            label: intl.formatMessage({
                              id: 'FILTER.INPUT.VALUE.ANNOTATION',
                            }),
                          },
                          {
                            value: 'vinne_verification',
                            label: intl.formatMessage({
                              id: 'FILTER.INPUT.VALUE.VERIFICATION',
                            }),
                          },
                        ]}
                        placeholderMessage={'FILTER.INPUT.RECOGNITION'}
                        onChange={(value) =>
                          handleChangeFilter({ recognition: value })
                        }
                      />
                    </Inputbox>
                    {renderResetFilterButton({
                      isVisible:
                        filterStore.recognition !== undefined
                          ? true
                          : undefined,
                      onClick: () =>
                        handleChangeFilter({
                          recognition: undefined,
                        }),
                    })}
                  </Item>
                  {filterDataSourcePhotoCategories.map((category, key) => (
                    <Item key={key}>
                      <Inputbox>
                        <Select
                          value={
                            (filterStore.tags &&
                              filterStore.tags[category.id]) ||
                            ''
                          }
                          options={
                            category &&
                            Object.entries(category.tags).length > 0 &&
                            Object.entries(category.tags).map((tag) => {
                              return {
                                value: tag[0],
                                label: tag[1] || tag[0],
                              };
                            })
                          }
                          placeholderMessage={category.name || category.id}
                          onChange={(tags) =>
                            handleChangeFilter({
                              tags: {
                                ...filterStore.tags,
                                [category.id]: tags,
                              },
                            })
                          }
                          isSearchable={true}
                          isSelectAll={true}
                          isMulti={true}
                          isCheckbox={true}
                          closeMenuOnSelect={false}
                          menuPosition={'relative'}
                        />
                      </Inputbox>
                      {renderResetFilterButton({
                        isVisible:
                          filterStore.tags &&
                          filterStore.tags[category.id] !== undefined
                            ? true
                            : undefined,
                        onClick: () =>
                          handleChangeFilter({
                            tags: {
                              ...filterStore.tags,
                              [category.id]: undefined,
                            },
                          }),
                      })}
                    </Item>
                  ))}
                </>
              ) : (
                ''
              )}
              {availableFilterMultilabeling &&
              filterDataSourceObjectsCategories &&
              isLoaded(filterDataSourceObjectsCategories) &&
              filterDataSourceObjectsCategories.length === 0
                ? renderMultilabelingSelection()
                : ''}
            </>
          )}
        </Content>
        <Grow />
        <Footer>
          <Button theme={theme.button.alt} onClick={() => handleResetFilter()}>
            {intl.formatMessage({ id: 'FILTER.RESET_FILTERS' })}
          </Button>
        </Footer>
      </Holder>
    </Wrapper>
  );
};

Filter.propTypes = {
  availableFilterAnnotated: PropTypes.bool,
  availableFilterVerified: PropTypes.bool,
  availableFilterRejected: PropTypes.bool,
  availableFilterReserved: PropTypes.bool,
  availableFilterTager: PropTypes.bool,
};

export default Filter;
